import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { Eye, EyeOff, ChevronDown, ChevronUp, User, Shield } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { updateProfileFields } from '../../redux/slices/authSlice';
import { useUpdateAccountMutation } from '../../redux/api/authApi';

const UserProfileTab: React.FC = () => {
  const profile = useAppSelector((s) => s.auth.profile);
  const dispatch = useDispatch<AppDispatch>();

  const [email] = useState<string>(profile.email ?? '');
  const [firstName, setFirstName] = useState<string>(profile.firstName ?? '');
  const [lastName, setLastName] = useState<string>(profile.lastName ?? '');
  const [phoneNumber, setPhoneNumber] = useState<string>(profile.phone ?? '');
  const [role, setRole] = useState<string>(profile.role ?? '');
  const [openSection, setOpenSection] = useState<'profile' | 'security'>('profile');
  const [isLoadingProfile] = useState<boolean>(false);
  const [isSavingProfile, setIsSavingProfile] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [updateAccount, { isLoading: isUpdatingAccount }] = useUpdateAccountMutation();

  // Security state
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const rules = useMemo(() => ({
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    match: confirmPassword.length > 0 && password === confirmPassword,
  }), [password, confirmPassword]);

  useEffect(() => {
    setFirstName(profile.firstName ?? '');
    setLastName(profile.lastName ?? '');
    setRole(profile.role ?? '');
    setPhoneNumber(profile.phone ?? '');
    setHasChanges(false);
  }, [profile.firstName, profile.lastName, profile.role, profile.phone]);

  useEffect(() => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedRole = role.trim();
    const trimmedPhone = phoneNumber.trim();

    const hasDiff =
      trimmedFirstName !== (profile.firstName ?? '') ||
      trimmedLastName !== (profile.lastName ?? '') ||
      trimmedRole !== (profile.role ?? '') ||
      trimmedPhone !== (profile.phone ?? '');

    setHasChanges(hasDiff);
  }, [firstName, lastName, role, phoneNumber, profile.firstName, profile.lastName, profile.role, profile.phone]);

  const handleSaveProfile = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedRole = role.trim();
    const trimmedPhoneNumber = phoneNumber.trim();

    if (!trimmedFirstName || !trimmedLastName) {
      toast.error('First and last name are required');
      return;
    }

    setIsSavingProfile(true);
    try {
      const payload = {
        first_name: trimmedFirstName,
        last_name: trimmedLastName,
        role: trimmedRole || undefined,
        phone: trimmedPhoneNumber || undefined,
      };

      await updateAccount(payload).unwrap();
      toast.success('Profile updated successfully');
      dispatch(
        updateProfileFields({
          firstName: trimmedFirstName || null,
          lastName: trimmedLastName || null,
          phone: trimmedPhoneNumber || null,
          role: trimmedRole || null,
        })
      );
      setHasChanges(false);
    } catch (error: any) {
      console.warn('Profile update failed:', error);
      toast.error(error?.data?.message || 'Failed to update profile');
    }
    setIsSavingProfile(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rules.length || !rules.uppercase || !rules.number || !rules.special || !rules.match) {
      toast.error('Password does not meet requirements');
      return;
    }
    setIsUpdatingPassword(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      toast.error(error.message || 'Failed to update password');
      setIsUpdatingPassword(false);
      return;
    }
    toast.success('Password updated');
    setPassword('');
    setConfirmPassword('');
    setIsUpdatingPassword(false);
  };

  return (
    <div className="h-full w-full overflow-auto bg-engramiq-dark-blue p-6">
      <div className="max-w-2xl mx-auto">
        {/* Profile section */}
        <div className="bg-surface-800/50 rounded-xl border border-engramiq-green/20 mb-4 overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 text-engramiq-light-grey hover:bg-white/10"
            onClick={() => setOpenSection(openSection === 'profile' ? 'security' : 'profile')}
            aria-expanded={openSection === 'profile'}
          >
            <span className="flex items-center gap-2 text-base font-semibold">
              <User className="w-4 h-4" />
              Personal Information
            </span>
            {openSection === 'profile' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {openSection === 'profile' && (
            <div className="p-6 border-t border-engramiq-green/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm text-engramiq-bright-grey mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    disabled
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-engramiq-light-grey disabled:opacity-70"
                  />
                </div>
                <div>
                  <label className="block text-sm text-engramiq-bright-grey mb-1">First Name</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-engramiq-light-grey focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
                    placeholder="Enter your first name"
                    disabled={isLoadingProfile}
                  />
                </div>
                <div>
                  <label className="block text-sm text-engramiq-bright-grey mb-1">Last Name</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-engramiq-light-grey focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
                    placeholder="Enter your last name"
                    disabled={isLoadingProfile}
                  />
                </div>
                <div>
                  <label className="block text-sm text-engramiq-bright-grey mb-1">Role</label>
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-engramiq-light-grey focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
                    placeholder="e.g., admin, operator"
                    disabled={isLoadingProfile}
                  />
                </div>
                <div>
                  <label className="block text-sm text-engramiq-bright-grey mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-engramiq-light-grey focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
                    placeholder="Enter your phone number"
                    disabled={isLoadingProfile}
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-engramiq-light-grey border border-white/20 transition-colors disabled:opacity-60"
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile || isLoadingProfile || !hasChanges || isUpdatingAccount}
                >
                  {isSavingProfile || isUpdatingAccount ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Security section */}
        <div className="bg-surface-800/50 rounded-xl border border-engramiq-green/20 overflow-hidden">
          <button
            className="w-full flex items-center justify-between px-4 py-3 text-engramiq-light-grey hover:bg-white/10"
            onClick={() => setOpenSection(openSection === 'security' ? 'profile' : 'security')}
            aria-expanded={openSection === 'security'}
          >
            <span className="flex items-center gap-2 text-base font-semibold">
              <Shield className="w-4 h-4" />
              Privacy and Security
            </span>
            {openSection === 'security' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {openSection === 'security' && (
            <div className="p-6 border-t border-engramiq-green/20">
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="text-engramiq-light-grey">Change Password</div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2.5 pr-10 rounded-lg text-white placeholder-white/90 border border-white/30 transition-all duration-300 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none bg-white/10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-3 py-2.5 pr-10 rounded-lg text-white placeholder-white/90 border border-white/30 transition-all duration-300 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none bg-white/10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <ul className="text-xs text-engramiq-bright-grey space-y-1">
                  <li className={rules.length ? 'text-green-400' : ''}>At least 8 characters</li>
                  <li className={rules.uppercase ? 'text-green-400' : ''}>At least one uppercase letter</li>
                  <li className={rules.number ? 'text-green-400' : ''}>At least one number</li>
                  <li className={rules.special ? 'text-green-400' : ''}>At least one special character</li>
                  <li className={rules.match ? 'text-green-400' : ''}>Passwords match</li>
                </ul>
                <button
                  type="submit"
                  disabled={isUpdatingPassword}
                  className="w-full py-2.5 rounded-lg font-semibold tracking-wide transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'rgba(23, 196, 128, 0.9)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    color: '#0d1830'
                  }}
                >
                  {isUpdatingPassword ? 'Updating…' : 'Update password'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfileTab;


